/**
 * One selectable filter column. `label` is what the user picks by — the
 * column's own title, so the picker reads like the grid it filters — and
 * `value` is the column name the filter is sent as. The two differ on the
 * review page's export worksheets, whose headings ("PRODUCT NUMBER") are not
 * their AS/400 field codes ("ITEMNO").
 */
export interface FilterColumnOption {
  value: string;
  label: string;
  /** Secondary text beside the label (a description, or the field code). */
  hint?: string | null;
}

export interface ColumnComboboxBase {
  name: string;
  label: string;
}

export interface ColumnComboboxState {
  matchedBase: ColumnComboboxBase[];
  matchedColumns: FilterColumnOption[];
  showCustom: boolean;
  showEmpty: boolean;
  showLoading: boolean;
  trimmed: string;
  selectedLabel: string | null;
}

/**
 * What the advanced-filter column picker should show for the current search
 * and selection. Extracted so ColumnCombobox stays hooks + JSX.
 */
export function columnComboboxState({
  search,
  value,
  columns,
  columnsLoading,
  baseColumns,
}: {
  search: string;
  value: string;
  columns: readonly FilterColumnOption[];
  columnsLoading: boolean;
  baseColumns: readonly ColumnComboboxBase[];
}): ColumnComboboxState {
  const q = search.trim().toLowerCase();
  const matchedBase = baseColumns.filter(
    (column) =>
      column.name.toLowerCase().includes(q) ||
      column.label.toLowerCase().includes(q),
  );
  const matchedColumns = columns.filter(
    (column) =>
      column.label.toLowerCase().includes(q) ||
      column.value.toLowerCase().includes(q) ||
      (column.hint ?? '').toLowerCase().includes(q),
  );

  const trimmed = search.trim();
  const exactMatch =
    baseColumns.some(
      (column) => column.name === trimmed || column.label === trimmed,
    ) ||
    columns.some(
      (column) => column.value === trimmed || column.label === trimmed,
    );

  return {
    matchedBase,
    matchedColumns,
    showCustom: trimmed.length > 0 && !exactMatch,
    showEmpty: !columnsLoading,
    showLoading: columnsLoading && matchedColumns.length === 0,
    trimmed,
    selectedLabel: selectedColumnLabel(value, columns, baseColumns),
  };
}

function selectedColumnLabel(
  value: string,
  columns: readonly FilterColumnOption[],
  baseColumns: readonly ColumnComboboxBase[],
): string | null {
  if (!value) return null;
  const base = baseColumns.find((column) => column.name === value);
  if (base) return base.label;
  return columns.find((column) => column.value === value)?.label ?? value;
}
